import styled from 'styled-components';
import { Field, ErrorMessage as FormikErrorMessage } from 'formik';

/**
 * FormElements — CHANGED in 1.17.0-dev.1 (Vikar Experten design system).
 *
 * Summary of the visual change:
 *   - Panels (FormSection) are hairline-bordered with 6px corners and the
 *     faintest app lift — the client's approved geometry. The old 8px radius +
 *     two-layer drop shadow is gone.
 *   - Controls sit a step tighter at 4px, the radius the design system sanctions
 *     for dense app controls.
 *   - Focus is a 3px halo in the control's action colour, on a matching border.
 *     The website has no focus state at all, which is not acceptable in an
 *     internal tool.
 *   - Labels are bold (the brand has no semibold) and stay SENTENCE case —
 *     uppercase is for headings, CTA labels, navigation and badges only.
 *   - Checkbox and radio marks are brand red, and the checkbox is square.
 *   - FormAlert is now drawn the way the brand draws a form response: coloured
 *     text inside a 3px border on white, never a filled pastel banner.
 *   - Buttons follow the client's action semantics: FormButton is blue by
 *     default (save / send / submit), variant="accent" is the green approve,
 *     variant="danger" is the red reject. Hover darkens the button's own fill.
 *
 * No component was renamed, removed, or changed in props.
 */

export const FormContainer = styled.div`
  width:100%;
  max-width:${({ fullWidth, theme }) => (fullWidth ? '100%' : theme.grid.containerWidth.sm)};
  margin:${({ margin }) => margin || '0 auto'};
`;

export const FormSection = styled.div`
  margin-bottom:${({ theme }) => theme.spacing[8]};
  padding:${({ theme }) => theme.spacing[6]};
  background-color:${({ theme }) => theme.colors.white};
  border:${({ theme }) => theme.components.card.border};
  border-radius:${({ theme }) => theme.components.card.borderRadius};
  box-shadow:${({ theme }) => theme.components.card.boxShadow};
`;

export const FormSectionTitle = styled.h2`
  font-size:${({ theme }) => theme.typography.fontSize.md};
  text-transform:uppercase;
  letter-spacing:${({ theme }) => theme.typography.letterSpacing.normal};
  margin-bottom:${({ theme }) => theme.spacing[5]};
  color:${({ theme }) => theme.colors.dark};
  font-weight:${({ theme }) => theme.typography.fontWeight.bold};
`;

export const FormGroup = styled.div`
  margin-bottom:${({ theme, marginBottom }) => marginBottom || theme.spacing[5]};
`;

export const FormRow = styled.div`
  display:flex;
  flex-wrap:wrap;
  margin:0 -${({ theme }) => theme.spacing[2]};

  ${FormGroup}{
    flex:1 0 0%;
    padding:0 ${({ theme }) => theme.spacing[2]};
    min-width:${({ columns }) => (columns ? `calc(100% / ${columns})` : '100%')};

    @media (max-width:768px){
      min-width:100%;
      margin-bottom:${({ theme }) => theme.spacing[3]};
    }
  }
`;

export const Label = styled.label`
  display:block;
  color:${({ theme }) => theme.components.form.label.color};
  font-size:${({ theme }) => theme.components.form.label.fontSize};
  font-weight:${({ theme }) => theme.components.form.label.fontWeight};
  margin-bottom:${({ theme }) => theme.components.form.label.marginBottom};
`;

export const HelpText = styled.div`
  font-size:${({ theme }) => theme.typography.fontSize.xs};
  font-weight:${({ theme }) => theme.typography.fontWeight.light};
  color:${({ theme }) => theme.colors.textSecondary};
  margin-top:${({ theme }) => theme.spacing[1]};
`;

export const Input = styled(Field)`
  width:100%;
  min-height:44px; /* accessible target; 16px base also prevents iOS zoom */
  padding:${({ theme }) => theme.components.form.input.padding};
  font-size:${({ theme }) => theme.components.form.input.fontSize};
  font-weight:${({ theme }) => theme.typography.fontWeight.light};
  color:${({ theme }) => theme.colors.text};
  background-color:${({ theme }) => theme.colors.white};
  border:1px solid ${({ theme, error }) => (error ? theme.colors.error : theme.colors.border)};
  border-radius:${({ theme }) => theme.borders.radius.sm};
  transition:${({ theme }) => theme.transitions.default};

  &:focus{
    outline:none;
    border-color:${({ theme, error }) => (error ? theme.colors.error : theme.colors.primary)};
    box-shadow:${({ theme, error }) =>
      error ? '0 0 0 3px rgba(195, 37, 29, 0.25)' : theme.shadows.focus};
  }

  &::placeholder{color:${({ theme }) => theme.colors.brand.ink400};opacity:1}

  &:disabled{
    background-color:${({ theme }) => theme.colors.light};
    color:${({ theme }) => theme.colors.brand.ink400};
    cursor:not-allowed;
  }
`;

export const Textarea = styled(Input).attrs({ forwardedAs: 'textarea' })`
  min-height:100px;
  line-height:${({ theme }) => theme.typography.lineHeight.normal};
  resize:vertical;
`;

/* The caret is drawn in the brand's ink rather than the old blue-grey. */
export const Select = styled(Field).attrs({ forwardedAs: 'select' })`
  width:100%;
  min-height:44px;
  padding:${({ theme }) => theme.components.form.input.padding};
  font-size:${({ theme }) => theme.components.form.input.fontSize};
  font-weight:${({ theme }) => theme.typography.fontWeight.light};
  color:${({ theme }) => theme.colors.text};
  background-color:${({ theme }) => theme.colors.white};
  border:1px solid ${({ theme, error }) => (error ? theme.colors.error : theme.colors.border)};
  border-radius:${({ theme }) => theme.borders.radius.sm};
  transition:${({ theme }) => theme.transitions.default};
  appearance:none;
  background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%2319242e' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
  background-repeat:no-repeat;
  background-position:right ${({ theme }) => theme.spacing[4]} center;
  padding-right:${({ theme }) => theme.spacing[8]};

  &:focus{
    outline:none;
    border-color:${({ theme, error }) => (error ? theme.colors.error : theme.colors.primary)};
    box-shadow:${({ theme, error }) =>
      error ? '0 0 0 3px rgba(195, 37, 29, 0.25)' : theme.shadows.focus};
  }

  &:disabled{
    background-color:${({ theme }) => theme.colors.light};
    color:${({ theme }) => theme.colors.brand.ink400};
    cursor:not-allowed;
  }
`;

export const CheckboxContainer = styled.div`
  display:flex;
  align-items:flex-start;
  margin-bottom:${({ theme }) => theme.spacing[3]};
`;

export const HiddenCheckbox = styled(Field).attrs({ type: 'checkbox' })`
  position:absolute;
  opacity:0;
  height:0;
  width:0;
`;

/* 4px, matching the inputs it sits beside. */
export const CustomCheckbox = styled.div`
  display:inline-flex;
  align-items:center;
  justify-content:center;
  width:20px;
  height:20px;
  flex:0 0 20px;
  margin-right:${({ theme }) => theme.spacing[3]};
  background-color:${({ checked, theme }) => (checked ? theme.colors.primary : theme.colors.white)};
  border:1px solid ${({ checked, theme }) => (checked ? theme.colors.primary : theme.colors.border)};
  border-radius:${({ theme }) => theme.borders.radius.sm};
  transition:${({ theme }) => theme.transitions.default};

  &:after{
    content:'';
    display:${({ checked }) => (checked ? 'block' : 'none')};
    width:5px;
    height:10px;
    border:solid ${({ theme }) => theme.colors.white};
    border-width:0 2px 2px 0;
    transform:rotate(45deg) translate(-1px, -1px);
  }
`;

export const CheckboxLabel = styled.label`
  font-size:${({ theme }) => theme.typography.fontSize.base};
  font-weight:${({ theme }) => theme.typography.fontWeight.light};
  color:${({ theme }) => theme.colors.text};
  user-select:none;
  cursor:pointer;
`;

export const RadioContainer = styled.div`
  margin-bottom:${({ theme }) => theme.spacing[4]};
`;

export const RadioGroup = styled.div`
  display:flex;
  flex-direction:${({ inline }) => (inline ? 'row' : 'column')};
  gap:${({ theme, inline }) => (inline ? theme.spacing[5] : theme.spacing[3])};
`;

export const RadioItem = styled.label`
  display:flex;
  align-items:center;
  font-size:${({ theme }) => theme.typography.fontSize.base};
  font-weight:${({ theme }) => theme.typography.fontWeight.light};
  color:${({ theme }) => theme.colors.text};
  cursor:pointer;
`;

export const HiddenRadio = styled(Field).attrs({ type: 'radio' })`
  position:absolute;
  opacity:0;
  height:0;
  width:0;
`;

/* The radio stays round — a circle is the affordance, not a brand decision. */
export const CustomRadio = styled.div`
  display:inline-flex;
  align-items:center;
  justify-content:center;
  width:20px;
  height:20px;
  flex:0 0 20px;
  margin-right:${({ theme }) => theme.spacing[3]};
  background-color:${({ theme }) => theme.colors.white};
  border:1px solid ${({ checked, theme }) => (checked ? theme.colors.primary : theme.colors.border)};
  border-radius:${({ theme }) => theme.borders.radius.full};
  transition:${({ theme }) => theme.transitions.default};

  &:after{
    content:'';
    display:${({ checked }) => (checked ? 'block' : 'none')};
    width:10px;
    height:10px;
    border-radius:${({ theme }) => theme.borders.radius.full};
    background-color:${({ theme }) => theme.colors.primary};
  }
`;

export const ErrorMessage = styled(({ name, component, ...props }) => (
  <FormikErrorMessage name={name} component={component || 'div'} {...props} />
))`
  color:${({ theme }) => theme.colors.error};
  font-size:${({ theme }) => theme.typography.fontSize.xs};
  font-weight:${({ theme }) => theme.typography.fontWeight.regular};
  margin-top:${({ theme }) => theme.spacing[1]};
`;

export const FormButton = styled.button`
  display:inline-flex;
  align-items:center;
  justify-content:center;
  gap:${({ theme }) => theme.spacing[2]};
  padding:${({ size, theme }) =>
    size === 'sm' ? `${theme.spacing[2]} ${theme.spacing[3]}` :
    size === 'lg' ? `${theme.spacing[4]} ${theme.spacing[6]}` :
    `${theme.spacing[3]} ${theme.spacing[5]}`};
  font-size:${({ size, theme }) =>
    size === 'sm' ? theme.typography.fontSize.xs :
    size === 'lg' ? theme.typography.fontSize.md :
    theme.typography.fontSize.base};
  font-weight:${({ theme }) => theme.typography.fontWeight.bold};
  text-transform:uppercase;
  letter-spacing:${({ theme }) => theme.typography.letterSpacing.cta};
  border-radius:${({ theme }) => theme.borders.radius.content};
  transition:${({ theme }) => theme.transitions.default};
  cursor:pointer;
  width:${({ fullWidth }) => (fullWidth ? '100%' : 'auto')};
  min-height:44px;

  background-color:${({ theme, variant }) =>
    variant === 'secondary' ? theme.components.button.secondary.backgroundColor :
    variant === 'accent' ? theme.components.button.accent.backgroundColor :
    variant === 'danger' || variant === 'outline' || variant === 'ghost' ? 'transparent' :
    theme.components.button.primary.backgroundColor};
  color:${({ theme, variant }) =>
    variant === 'accent' ? theme.components.button.accent.color :
    variant === 'danger' ? theme.components.button.danger.color :
    variant === 'outline' || variant === 'ghost' ? theme.components.button.outline.color :
    theme.colors.white};
  border:${({ theme, variant }) =>
    variant === 'danger' ? `1px solid ${theme.components.button.danger.borderColor}` :
    variant === 'outline' ? `1px solid ${theme.components.button.outline.borderColor}` : 'none'};

  &:hover{
    background-color:${({ theme, variant }) =>
      variant === 'accent' ? theme.components.button.accent.hoverBackgroundColor :
      variant === 'danger' ? theme.components.button.danger.hoverBackgroundColor :
      variant === 'outline' ? theme.components.button.outline.hoverBackgroundColor :
      variant === 'ghost' ? theme.components.button.ghost.hoverBackgroundColor :
      theme.components.button.primary.hoverBackgroundColor};
    border-color:${({ theme, variant }) =>
      variant === 'danger' ? theme.components.button.danger.hoverBackgroundColor :
      variant === 'outline' ? theme.components.button.outline.hoverBackgroundColor : 'transparent'};
    color:${({ theme, variant }) =>
      variant === 'accent' ? theme.components.button.accent.hoverColor :
      variant === 'ghost' ? theme.components.button.ghost.hoverColor :
      theme.colors.white};
    box-shadow:${({ theme, variant }) => (variant === 'ghost' ? 'none' : theme.shadows.press)};
  }

  &:focus-visible{
    outline:none;
    box-shadow:${({ theme, variant }) =>
      variant === 'accent' ? theme.shadows.focusAccent :
      variant === 'danger' ? theme.shadows.focusDanger :
      theme.shadows.focus};
  }
  &:disabled{opacity:.6;cursor:not-allowed;box-shadow:none}

  svg{width:1em;height:1em}
`;

export const FormActions = styled.div`
  display:flex;
  gap:${({ theme }) => theme.spacing[3]};
  justify-content:${({ align }) =>
    align === 'left' ? 'flex-start' :
    align === 'right' ? 'flex-end' :
    align === 'between' ? 'space-between' :
    'center'};
  margin-top:${({ theme }) => theme.spacing[6]};

  @media (max-width:576px){
    flex-direction:${({ stack }) => (stack ? 'column' : 'row')};
    ${({ stack }) => stack && `${FormButton}{width:100%}`}
  }
`;

export const FormDivider = styled.div`
  height:1px;
  background-color:${({ theme }) => theme.colors.hairline};
  margin:${({ theme }) => theme.spacing[6]} 0;
`;

/**
 * FormAlert — the brand's form response: coloured text inside a 3px border on
 * white. The source draws its success message exactly this way and never uses a
 * filled banner, so the old pastel fills (#D4EDDA / #FFF3CD / #F8D7DA) are gone.
 */
export const FormAlert = styled.div`
  padding:${({ theme }) => theme.spacing[4]};
  margin-bottom:${({ theme }) => theme.spacing[5]};
  border-radius:${({ theme }) => theme.borders.radius.content};
  font-size:${({ theme }) => theme.typography.fontSize.base};
  font-weight:${({ theme }) => theme.typography.fontWeight.regular};
  background-color:${({ theme }) => theme.colors.white};
  border:3px solid ${({ theme, variant }) =>
    variant === 'success' ? theme.colors.success :
    variant === 'warning' ? theme.colors.warning :
    variant === 'error' ? theme.colors.error :
    theme.colors.info};
  color:${({ theme, variant }) =>
    variant === 'success' ? theme.colors.success :
    variant === 'warning' ? theme.colors.statusPlate.warningInk :
    variant === 'error' ? theme.colors.error :
    theme.colors.info};

  ${({ hasIcon, theme }) => hasIcon && `
    display:flex;
    align-items:flex-start;
    gap:${theme.spacing[3]};
    svg{flex-shrink:0;margin-top:2px}
  `}
`;
